import React from 'react';
import Slider from 'react-slick';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';

const reviewsIT = [
    {
        score: '10',
        text: "Struttura meravigliosa, camere pulitissime e colazione da sogno. I gestori sono di una gentilezza rara, ci hanno dato ottimi consigli su dove mangiare e cosa visitare. Torneremo sicuramente!",
        name: "Marco R.",
        origin: "Italia",
        source: "Booking.com",
        initial: "M",
    },
    {
        score: '10',
        text: "Abbiamo soggiornato nella Suite Jacuzzi per il nostro anniversario ed è stata un'esperienza indimenticabile. La vasca con cromoterapia è semplicemente stupenda, tutto curatissimo nei minimi dettagli.",
        name: "Valentina M.",
        origin: "Italia",
        source: "Booking.com",
        initial: "V",
    },
    {
        score: '10',
        text: "Posizione ottima tra Anzio e Nettuno, a pochi minuti dal mare. Camera spaziosa e silenziosa, colazione ottima con prodotti freschi, parcheggio riservato comodissimo. Una delle migliori strutture della zona.",
        name: "Francesco D.",
        origin: "Roma, Italia",
        source: "Booking.com",
        initial: "F",
    },
    {
        score: '10',
        text: "Accoglienza calorosa fin dal primo momento. La camera era impeccabile, il letto comodissimo e la colazione abbondante e gustosa. Si percepisce la cura e l'attenzione dei proprietari in ogni dettaglio.",
        name: "Giulia P.",
        origin: "Milano, Italia",
        source: "Booking.com",
        initial: "G",
    },
    {
        score: '9',
        text: "Soggiorno molto piacevole. La struttura è nuova, moderna e curatissima. La suite jacuzzi è qualcosa di speciale. Ottimo rapporto qualità-prezzo per la zona costiera laziale.",
        name: "Lorenzo B.",
        origin: "Napoli, Italia",
        source: "Booking.com",
        initial: "L",
    },
    {
        score: '10',
        text: "Abbiamo trascorso un weekend meraviglioso. I gestori sono stati disponibili e gentilissimi, ci hanno anche consigliato i migliori ristoranti di pesce di Anzio. La colazione ogni mattina era una gioia.",
        name: "Claudia e Stefano",
        origin: "Firenze, Italia",
        source: "Booking.com",
        initial: "C",
    },
];

const reviewsEN = [
    {
        score: '10',
        text: "A wonderful property, spotlessly clean rooms and a dream breakfast. The hosts are incredibly kind — great tips on where to eat and what to see in Anzio. We will definitely be back!",
        name: "Marco R.",
        origin: "Italy",
        source: "Booking.com",
        initial: "M",
    },
    {
        score: '10',
        text: "We stayed in the Jacuzzi Suite for our anniversary and it was unforgettable. The chromotherapy jacuzzi is simply stunning, every detail perfectly curated. Highly recommended!",
        name: "Valentina M.",
        origin: "Italy",
        source: "Booking.com",
        initial: "V",
    },
    {
        score: '10',
        text: "Great location between Anzio and Nettuno, just minutes from the sea. Spacious, quiet room, excellent fresh breakfast, very convenient reserved parking. One of the best stays in the area.",
        name: "Francesco D.",
        origin: "Rome, Italy",
        source: "Booking.com",
        initial: "F",
    },
    {
        score: '10',
        text: "Warm welcome from the very first moment. The room was impeccable, the bed incredibly comfortable and the breakfast generous and delicious. You can feel the owners' care and attention in every detail.",
        name: "Giulia P.",
        origin: "Milan, Italy",
        source: "Booking.com",
        initial: "G",
    },
    {
        score: '9',
        text: "Very pleasant stay. The property is brand new, modern and beautifully maintained. The jacuzzi suite is something special. Excellent value for money on the Lazio coast.",
        name: "Lorenzo B.",
        origin: "Naples, Italy",
        source: "Booking.com",
        initial: "L",
    },
    {
        score: '10',
        text: "We had a wonderful weekend. The hosts were helpful and very kind — they even recommended the best seafood restaurants in Anzio. The breakfast every morning was a real treat.",
        name: "Claudia & Stefano",
        origin: "Florence, Italy",
        source: "Booking.com",
        initial: "C",
    },
];

function ReviewCard({ review }) {
    return (
        <div className="review-card">
            <div className="review-top">
                <div className="review-stars">★★★★★</div>
                <div className="review-score-badge">{review.score}</div>
            </div>
            <p className="review-text">"{review.text}"</p>
            <div className="review-author">
                <div className="review-avatar">{review.initial}</div>
                <div>
                    <span className="review-name">{review.name}</span>
                    <span className="review-origin">{review.origin}</span>
                    <span className="review-source">{review.source}</span>
                </div>
            </div>
        </div>
    );
}

function Reviews() {
    const { isEnLang } = useLanguage();
    const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.05 });

    const reviews = isEnLang ? reviewsEN : reviewsIT;

    const T = isEnLang
        ? { label: 'Guest Reviews', title: 'What Our Guests Say' }
        : { label: 'Recensioni Ospiti', title: 'Cosa Dicono i Nostri Ospiti' };

    const sliderSettings = {
        dots: false,
        arrows: false,
        infinite: true,
        speed: 600,
        slidesToShow: 1,
        slidesToScroll: 1,
        swipeToSlide: true,
    };

    return (
        <section className="reviews">
            <div className="reviews-header">
                <span className="section-label">{T.label}</span>
                <h2>{T.title}</h2>
            </div>

            {/* grid: desktop + tablet */}
            <motion.div
                ref={ref}
                className="reviews-grid"
                initial={{ opacity: 0, y: 48 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8 }}
            >
                {reviews.map((review, i) => (
                    <ReviewCard key={i} review={review} />
                ))}
            </motion.div>

            {/* slider: mobile only */}
            <div className="reviews-slider">
                <Slider {...sliderSettings}>
                    {reviews.map((review, i) => (
                        <div key={i} className="reviews-slide-wrap">
                            <ReviewCard review={review} />
                        </div>
                    ))}
                </Slider>
            </div>
        </section>
    );
}

export default Reviews;
